import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { messageStoreKey } from "../../utils/clientDetailsCategory";
import { useLocation } from "react-router-dom";
import { Paper } from "@material-ui/core";
import Empty from "antd/es/empty";
import { MESSAGE_ACTION } from "../../redux/action";
import { dateTimeFormatter, Toaster } from "../../utils/common";
import MessageChatInput from "../../components/MessageChatInput";
import MessageChatRow from "../../components/MessageChatRow";
import { useClientCategoryTab } from "../../hooks/useClientCategoryTab";
import { filterRecordsByClientCategory } from "../../utils/clientDetailsCategory";
import "./messages.scss";

const Messages = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const USER_ROLE = localStorage.getItem("user_role");
  const isClient = USER_ROLE === "client";
  const loggedInUserId = localStorage.getItem("user_id");

  const { unreadSummary, threads, messagesByClientId } = useSelector(
    (s) => s.message || {},
  );

  const [filter, setFilter] = useState("unread"); // unread | all
  const [activeClientId, setActiveClientId] = useState(() =>
    isClient ? String(loggedInUserId || "") : null,
  );
  const [messageText, setMessageText] = useState("");
  const [sending, setSending] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const chatListRef = useRef(null);

  const {
    categoryTab,
    showCategoryChooser,
    syncClientType,
  } = useClientCategoryTab();

  useEffect(() => {
    try {
      const userData = JSON.parse(localStorage.getItem("USER_DATA") || "{}");
      syncClientType(userData.client_type);
    } catch {
      /* ignore */
    }
  }, [syncClientType]);

  useEffect(() => {
    dispatch(MESSAGE_ACTION.getUnreadMessageSummary());
    dispatch(MESSAGE_ACTION.getMessageThreads());
  }, [dispatch]);

  // If navigated from a notification, auto-open thread (clients always use own id)
  useEffect(() => {
    const cid = location?.state?.client_id;
    if (!cid) return;
    setActiveClientId(
      isClient ? String(loggedInUserId || cid) : String(cid),
    );
  }, [location?.state, isClient, loggedInUserId]);

  const list = useMemo(() => {
    const base =
      filter === "unread" ? unreadSummary?.threads : threads?.threads;
    const arr = Array.isArray(base) ? base : [];
    // For client, always show a single thread entry so they can start the conversation.
    if (isClient) {
      const cid = String(loggedInUserId || "");
      if (!cid) return arr;
      const existing = arr.find((t) => String(t?.client_id) === cid);
      if (existing) return [existing];
      return [
        {
          client_id: cid,
          client_name: "My Associate",
          last_message: "",
          last_at: null,
          unread_count: 0,
        },
      ];
    }
    return arr;
  }, [filter, unreadSummary, threads]);

  useEffect(() => {
    if (!activeClientId) {
      if (isClient && loggedInUserId) setActiveClientId(String(loggedInUserId));
      return;
    }
    dispatch(
      MESSAGE_ACTION.getClientMessages({
        clientId: activeClientId,
        category: categoryTab,
      }),
    );
    dispatch(
      MESSAGE_ACTION.markClientThreadRead({
        clientId: activeClientId,
        category: categoryTab,
      }),
    );
  }, [dispatch, activeClientId, categoryTab]);

  const activeMessages = useMemo(() => {
    if (!activeClientId) return [];
    const stored =
      messagesByClientId?.[messageStoreKey(activeClientId, categoryTab)] || [];
    if (!showCategoryChooser) return stored;
    return filterRecordsByClientCategory(stored, categoryTab);
  }, [messagesByClientId, activeClientId, categoryTab, showCategoryChooser]);

  useEffect(() => {
    const el = chatListRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [activeClientId, activeMessages.length]);

  const cancelEditMessage = () => {
    setEditingMessageId(null);
    setMessageText("");
  };

  const startEditMessage = (message) => {
    setEditingMessageId(message?.message_id ?? null);
    setMessageText(message?.message ?? "");
  };

  const sendOrSave = async () => {
    const text = messageText.trim();
    if (!text || !activeClientId) return;
    setSending(true);
    try {
      if (editingMessageId) {
        await dispatch(
          MESSAGE_ACTION.updateClientMessage({
            messageId: editingMessageId,
            clientId: activeClientId,
            text,
            category: categoryTab,
          }),
        ).unwrap();
        cancelEditMessage();
        Toaster.success("Message updated.");
      } else {
        await dispatch(
          MESSAGE_ACTION.sendClientMessage({
            clientId: activeClientId,
            text,
            category: categoryTab,
          }),
        ).unwrap();
        setMessageText("");
      }
      dispatch(
        MESSAGE_ACTION.getClientMessages({
          clientId: activeClientId,
          category: categoryTab,
        }),
      );
      dispatch(MESSAGE_ACTION.getUnreadMessageSummary());
      dispatch(MESSAGE_ACTION.getMessageThreads());
    } catch (e) {
      Toaster.error(
        editingMessageId ? "Failed to update message" : "Failed to send message",
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="messages-page">
      <div className="messages-layout">
        <Paper className="messages-sidebar">
          <div className="messages-sidebar-head">
            <div className="messages-title">Messages</div>
            <div className="messages-filter">
              <button
                type="button"
                className={`messages-filter-btn ${filter === "all" ? "is-active" : ""}`}
                onClick={() => setFilter("all")}
              >
                All
              </button>
              <button
                type="button"
                className={`messages-filter-btn ${filter === "unread" ? "is-active" : ""}`}
                onClick={() => setFilter("unread")}
              >
                Unread
              </button>
            </div>
          </div>

          <div className="messages-thread-list">
            {list.length ? (
              list.map((t) => (
                <button
                  key={t?.client_id}
                  type="button"
                  className={`messages-thread ${String(t?.client_id) === String(activeClientId) ? "is-active" : ""}`}
                  onClick={() => setActiveClientId(String(t.client_id))}
                >
                  <div className="messages-thread-left">
                    <div className="messages-thread-title">
                      {t?.client_name || "Client"}
                    </div>
                    <div className="messages-thread-preview">
                      {t?.last_message || ""}
                    </div>
                  </div>
                  <div className="messages-thread-right">
                    {t?.last_at ? (
                      <div className="messages-thread-time">
                        {dateTimeFormatter(t.last_at)}
                      </div>
                    ) : null}
                    {Number(t?.unread_count || 0) > 0 ? (
                      <div className="messages-unread-badge">
                        {Number(t.unread_count)}
                      </div>
                    ) : null}
                  </div>
                </button>
              ))
            ) : (
              <div className="messages-empty">
                <Empty
                  description={
                    filter === "unread" ? "No unread messages" : "No messages"
                  }
                />
              </div>
            )}
          </div>
        </Paper>

        <Paper className="messages-chat">
          {activeClientId ? (
            <>
              <div className="messages-chat-list" ref={chatListRef}>
                {activeMessages.length ? (
                  activeMessages.map((m) => (
                    <MessageChatRow
                      key={m?.message_id || `${m?.createdAt}-${m?.message}`}
                      message={m}
                      loggedInUserId={loggedInUserId}
                      formatDate={(msg) =>
                        msg?.createdAt ? dateTimeFormatter(msg.createdAt) : ""
                      }
                      onEdit={startEditMessage}
                    />
                  ))
                ) : (
                  <div className="messages-empty">
                    <Empty description="No messages yet" />
                  </div>
                )}
              </div>

              <MessageChatInput
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                onSend={sendOrSave}
                sending={sending}
                editing={Boolean(editingMessageId)}
                onCancelEdit={cancelEditMessage}
              />
            </>
          ) : (
            <div className="messages-empty">
              <Empty description="Select a conversation" />
            </div>
          )}
        </Paper>
      </div>
    </div>
  );
};

export default Messages;
